import { useState } from "react";
import { toast } from "sonner";
import { Upload, Pencil, Trash2, Save, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Panel, BrandButton } from "@/components/PageHeader";
import { Field } from "@/components/event-setup/BasicsSection";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api, errMsg } from "@/lib/api";
import { uploadPdf, fileSrc, fmtSize } from "@/lib/resources";

const blank = { title: "", year: String(new Date().getFullYear()), note: "" };
const payload = (f) => ({ title: f.title.trim(), year: f.year ? Number(f.year) : null, note: f.note.trim() });

const ReportFields = ({ f, set, prefix }) => <div className="grid sm:grid-cols-[1fr_120px] gap-3">
  <Field label="Title"><Input value={f.title} maxLength={160} onChange={(e) => set({ ...f, title: e.target.value })} data-testid={`${prefix}-title`} /></Field>
  <Field label="Year"><Input type="number" min="1950" max="2100" value={f.year} onChange={(e) => set({ ...f, year: e.target.value })} data-testid={`${prefix}-year`} /></Field>
  <Field label="Short note (optional)" className="sm:col-span-2"><Input value={f.note} maxLength={400} onChange={(e) => set({ ...f, note: e.target.value })} data-testid={`${prefix}-note`} /></Field>
</div>;

const AddReport = ({ onAdded }) => {
  const [f, setF] = useState(blank);
  const [file, setFile] = useState(null);
  const [pct, setPct] = useState(null);
  const save = async () => {
    try {
      setPct(0);
      const up = await uploadPdf(file, setPct);
      await api.post("/resources/reports", { ...payload(f), path: up.path });
      toast.success("Report published to Resources");
      setF(blank); setFile(null); onAdded();
    } catch (e) { toast.error(errMsg(e)); }
    setPct(null);
  };
  return <div className="space-y-3" data-testid="report-add-form">
    <ReportFields f={f} set={setF} prefix="report-add" />
    <Field label="PDF document (up to 50 MB)"><Input key={file ? "has" : "none"} type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="report-add-file" /></Field>
    {pct !== null && <div className="space-y-1" data-testid="report-upload-progress"><Progress value={pct} /><p className="text-xs text-gray-500">Uploading… {pct}%</p></div>}
    <BrandButton onClick={save} disabled={pct !== null || !file || f.title.trim().length < 2} data-testid="report-add-save"><Upload className="w-4 h-4" />Upload report</BrandButton>
  </div>;
};

const ReportRow = ({ r, onChanged, onDelete }) => {
  const [edit, setEdit] = useState(null);
  const save = async () => {
    try { await api.put(`/resources/reports/${r.id}`, payload(edit)); toast.success("Report updated"); setEdit(null); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (edit) return <li className="py-4 space-y-3" data-testid={`report-edit-${r.id}`}>
    <ReportFields f={edit} set={setEdit} prefix={`report-edit-${r.id}`} />
    <div className="flex gap-2"><BrandButton onClick={save} disabled={edit.title.trim().length < 2} data-testid={`report-edit-save-${r.id}`}><Save className="w-4 h-4" />Save</BrandButton><BrandButton variant="outline" onClick={() => setEdit(null)} data-testid={`report-edit-cancel-${r.id}`}><X className="w-4 h-4" />Cancel</BrandButton></div>
  </li>;
  return <li className="py-3 flex items-start justify-between gap-3" data-testid={`report-row-${r.id}`}>
    <div className="min-w-0"><a href={fileSrc(r.url)} target="_blank" rel="noreferrer" className="font-semibold hover:text-[#E60000]">{r.title}</a>
      <div className="text-xs text-gray-500">{r.year || "No year"} · {r.filename} · {fmtSize(r.size)}</div></div>
    <div className="flex gap-3 shrink-0 text-xs font-semibold">
      <button onClick={() => setEdit({ title: r.title, year: r.year ? String(r.year) : "", note: r.note || "" })} className="inline-flex items-center gap-1 hover:text-[#E60000]" data-testid={`report-edit-btn-${r.id}`}><Pencil className="w-3.5 h-3.5" />Edit</button>
      <button onClick={() => onDelete(r)} className="inline-flex items-center gap-1 text-[#B30000] hover:underline" data-testid={`report-delete-btn-${r.id}`}><Trash2 className="w-3.5 h-3.5" />Remove</button>
    </div>
  </li>;
};

export const ReportsManager = ({ reports, onChanged }) => {
  const [del, setDel] = useState(null);
  return <Panel title="GAMLS Reports" testId="reports-manager">
    <AddReport onAdded={onChanged} />
    <ul className="divide-y mt-6 border-t" data-testid="reports-list">
      {reports.length ? reports.map((r) => <ReportRow key={r.id} r={r} onChanged={onChanged} onDelete={setDel} />) : <li className="py-4 text-sm text-gray-500">No reports yet.</li>}
    </ul>
    <ConfirmDialog open={!!del} onClose={() => setDel(null)} title="Remove this report?" description={`"${del?.title}" will no longer appear on the Resources page.`} confirmLabel="Remove report" testId="report-delete"
      onConfirm={async () => { await api.delete(`/resources/reports/${del.id}`); toast.success("Report removed"); onChanged(); }} />
  </Panel>;
};
